import { Info, X } from 'lucide-react';
import { Button } from '../button';
import { cn } from '../../lib/cn';
import type { CompatibilityRedirectNoticeProps } from './compatibility-redirect-notice.types';

export function CompatibilityRedirectNotice({
  variant = 'moved',
  legacyPath,
  fromLabel,
  toLabel,
  retiresOn,
  action,
  dismissible = true,
  onDismiss,
  className,
}: CompatibilityRedirectNoticeProps) {
  const oldThing = fromLabel ?? legacyPath ?? 'This page';
  const newThing = toLabel ?? 'the current page';
  const message =
    variant === 'renamed'
      ? `${oldThing} is now ${newThing}. Everything you had is here.`
      : variant === 'merged'
        ? `${oldThing} is now part of ${newThing}.`
        : variant === 'bookmark' && retiresOn
          ? `This page moved. The old address stops working on ${retiresOn}.`
          : `This page moved. ${oldThing} is now under ${newThing}.`;

  return (
    <div
      data-component="compatibility-redirect-notice"
      data-variant={variant}
      role="note"
      className={cn(
        'flex items-start gap-2 rounded-[10px] border border-[var(--a-info-soft)] bg-[var(--a-info-soft)] px-3 py-2.5 text-[12px] leading-5 text-[var(--a-info)]',
        className,
      )}
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">
        <strong className="font-semibold">{message}</strong>
        {variant === 'merged' && action ? <span className="ml-1">{action}</span> : null}
      </p>
      {dismissible ? (
        <Button
          type="button"
          variant="quiet"
          size="dense"
          iconOnly
          aria-label="Dismiss"
          onClick={onDismiss}
          className="-mr-1 -mt-1 shrink-0"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
}

CompatibilityRedirectNotice.displayName = 'CompatibilityRedirectNotice';
